import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Subject, Subscription, takeUntil } from 'rxjs';
import { UploadState } from 'src/app/shared/enums/upload-state.enum';
import { ChangePassword } from 'src/app/shared/interfaces/change-password.interface';
import {
  FileInfo,
  UploadFile,
} from 'src/app/shared/interfaces/file-info.interface';
import { User } from 'src/app/shared/models/user';
import { AuthService } from 'src/app/shared/services/auth.service';
import { PasswordService } from 'src/app/shared/services/password.service';
import { UploadFileService } from 'src/app/shared/services/upload-file.service';
import { showNotification } from 'src/app/shared/components/app-notifications/app-notifications.component';
import { Router } from '@angular/router';

const mock: Array<UploadFile> = [
  new UploadFile(
    {
      id: 23454,
      name: 'example_updating_file',
      size: 12000,
    },
    new BehaviorSubject<number>(50),
    new BehaviorSubject<UploadState>(UploadState.uploading)
  ),
  new UploadFile(
    {
      id: 23434554,
      name: 'example_ready_file_1',
      size: 12000,
    },
    new BehaviorSubject<number>(0),
    new BehaviorSubject<UploadState>(UploadState.ready)
  ),
  new UploadFile(
    {
      id: 2345544,
      name: 'example_ready_file_2',
      size: 12000000,
    },
    new BehaviorSubject<number>(30),
    new BehaviorSubject<UploadState>(UploadState.ready)
  ),
  new UploadFile(
    {
      id: 23455444,
      name: 'example_ready_file_3',
      size: 120,
    },
    new BehaviorSubject<number>(100),
    new BehaviorSubject<UploadState>(UploadState.ready)
  ),
  new UploadFile(
    {
      id: 234,
      name: 'example_ready_file_3',
      size: 120,
    },
    new BehaviorSubject<number>(100),
    new BehaviorSubject<UploadState>(UploadState.canceled)
  ),
];

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.scss'],
})
export class AccountDetailsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public allowedTypes: string[] = ['.png', '.jpeg', '.gif'];
  public fileDropError?: string;
  public currentUser: User | null = null;
  public isLoading = true;
  public isEditingProfile = false;
  public profileForm!: FormGroup;

  changePasswordForm!: FormGroup;
  _passwordChangeSubscription?: Subscription;

  uploadingFiles: Array<UploadFile> = [...mock];

  constructor(
    private fb: FormBuilder,
    private uploadService: UploadFileService,
    private passwordService: PasswordService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForms();
    this.loadUserData();
  }

  ngOnDestroy(): void {
    this._passwordChangeSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUserData(): void {
    this.isLoading = true;
    this.authService.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          this.currentUser = user;
          this.updateProfileForm();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading user data:', error);
          this.isLoading = false;
          showNotification('error', 'Error', 'Failed to load user profile data.');
        }
      });
  }

  updateProfileForm(): void {
    if (this.currentUser?.name) {
      const nameParts = this.currentUser.name.split(' ');
      this.profileForm.patchValue({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: this.currentUser.email || '',
      });
    }

    if (!this.isEditingProfile) {
      this.profileForm.disable();
    }
  }

  initForms(): void {
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
    });

    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmNewPassword: ['', [Validators.required]],
    }, { validators: this.passwordMatchValidator });

    this.profileForm.disable();
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmNewPassword')?.value;

    if (newPassword !== confirmPassword) {
      form.get('confirmNewPassword')?.setErrors({ mismatch: true });
      return { passwordMismatch: true };
    }

    return null;
  }

  toggleEditProfile(): void {

      //this.isEditingProfile = !this.isEditingProfile;

      //if (this.isEditingProfile) {
      //  this.profileForm.enable();
      //} else {
      //  this.profileForm.disable();
      //  this.updateProfileForm();
      //}
      this.router.navigate(['/not-found']);
  }

  isProfileControlInvalid(controlName: string): boolean {
    const control = this.profileForm.controls[controlName];
    return control.touched && control.invalid;
  }

  isChangePasswordControlInvalid(controlName: string) {
    var control = this.changePasswordForm.controls[controlName];
    return control.touched && control.invalid;
  }

  getPasswordErrorMessage(controlName: string): string {
    const control = this.changePasswordForm.controls[controlName];

    if (control.hasError('required')) {
      return 'Password is required';
    }

    if (control.hasError('minlength')) {
      return 'Password must be at least 8 characters';
    }

    if (controlName === 'confirmNewPassword' && control.hasError('mismatch')) {
      return 'Passwords do not match';
    }

    return 'Invalid password';
  }

  saveProfileChanges(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    const formData = this.profileForm.value;
    const updatedUser = {
      ...this.currentUser,
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email
    } as User;

    this.isLoading = true;
    this.authService.updateUserProfile(updatedUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.currentUser = updatedUser;
          this.isEditingProfile = false;
          this.isLoading = false;
          showNotification('success', 'Success', 'Profile updated successfully.');
        },
        error: (error) => {
          console.error('Error updating profile:', error);
          this.isLoading = false;
          showNotification('error', 'Error', 'Failed to update profile.');
        }
      });
  }

  onChangePasswordSubmit() {
    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this._passwordChangeSubscription?.unsubscribe();
    this._passwordChangeSubscription = this.passwordService.changePassword(this.changePasswordForm.getRawValue() as ChangePassword).subscribe({
      next: e => {
        console.log(e);
        this.isLoading = false;
        showNotification('success', 'Success', 'Password changed successfully.');
        this.changePasswordForm.reset();
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        showNotification('error', 'Error', 'Failed to change password. Please check your current password.');
      },
      complete: () => {
        console.log('completed');
      },
    });
  }

  onFileSelected(file: File) {
    this.fileDropError = undefined;
    var newFile = new UploadFile({ name: file.name, size: file.size });

    // Simulate upload with a loading indicator
    this.uploadingFiles.push(newFile);
    this.isLoading = true;

    // Simulate successful upload after delay
    setTimeout(() => {
      newFile.state.next(UploadState.ready);
      this.isLoading = false;
      showNotification('success', 'Success', 'File uploaded successfully.');
    }, 1500);
  }

  onFileError(error: string) {
    this.fileDropError = error;
    showNotification('error', 'Upload Error', error);
  }

  cancel(uploadFile: UploadFile) {
    uploadFile.requestSubsciption?.unsubscribe();
    this.uploadingFiles = this.uploadingFiles.filter((e) => e !== uploadFile);
  }

  delete(fileInfo: FileInfo) {
    console.log(fileInfo);

    this.uploadingFiles = this.uploadingFiles.filter(
      (e) => e.fileInfo.id !== fileInfo.id
    );
    showNotification('info', 'File Deleted', 'File has been deleted.');
  }
}
